import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Loader2 } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { useSentXStats } from '../hooks';
import './WhataKitModal.css';

/**
 * NFTModal - Componente base reutilizable para modales de NFT
 * @param {boolean} isOpen - Estado de visibilidad del modal
 * @param {function} onClose - Función para cerrar el modal
 * @param {string} collectionKey - Clave de la colección (whatakit, watt, earlyPack)
 * @param {string} image - Imagen de la colección
 * @param {string} titleKey - Clave de traducción para el título
 * @param {string} descKey - Clave de traducción para la descripción
 * @param {string} benefitsKey - Clave de traducción para beneficios
 * @param {string} benefitsDescKey - Clave de traducción para descripción de beneficios
 * @param {string} benefitsIcon - Emoji para beneficios
 * @param {Array} marketplaces - Array de marketplaces con urls
 */
const NFTModal = ({ 
  isOpen, 
  onClose, 
  collectionKey,
  image,
  titleKey,
  descKey,
  benefitsKey,
  benefitsDescKey,
  benefitsIcon = '💎',
  marketplaces: marketplacesConfig
}) => {
  const { t } = useLanguage();
  const stats = useSentXStats();
  const collectionData = stats[collectionKey];
  const { loading } = stats;

  const marketplaces = marketplacesConfig.map(mp => ({
    ...mp,
    floorPrice: collectionData?.floorPrice?.[mp.priceKey] || 0
  }));

  const handleMarketplaceClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="whatakit-modal"
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", duration: 0.35 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div className="modal-title-section">
                <div className="modal-icon">
                  <img src={image} alt={t(titleKey)} className="modal-icon-gif" />
                </div>
                <div>
                  <h2 className="modal-title">{t(titleKey)}</h2>
                  <p className="modal-subtitle">{t('modals.chooseMarketplace')}</p>
                </div>
              </div>
              
              <button 
                className="modal-close-btn"
                onClick={onClose}
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>

            <div className="modal-content">
              <div className="marketplace-description">
                <p>{t(descKey)}</p>
              </div>
              
              <div className="marketplace-ctas">
                {marketplaces.map((marketplace) => (
                  <motion.button
                    key={marketplace.name}
                    className="marketplace-btn btn btn--primary"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleMarketplaceClick(marketplace.url)}
                  >
                    <ExternalLink size={18} />
                    <span className="btn-text">
                      {t(`modals.${marketplace.displayNameKey}`)}
                      <span className="floor-price-inline">
                        ({t('modals.floorPrice')} {loading ? <Loader2 size={12} className="inline-loader" /> : `${marketplace.floorPrice} HBAR`})
                      </span>
                    </span>
                  </motion.button>
                ))}
              </div>

              <div className="modal-footer">
                <div className="modal-note">
                  <p>
                    <strong>{benefitsIcon} {t(benefitsKey)}</strong> {t(benefitsDescKey)}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NFTModal;
